import { FC, PropsWithChildren } from 'react';
import { Modal } from 'antd';
import styled, { CSSProperties } from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { ModalTitle } from './ModalTitle';

export type ModalContainerProps = {
  title: string;
  onClose?: () => void;
} & PropsWithChildren;

const DESKTOP_STYLES = {
  content: {
    maxWidth: 1100,
    maxHeight: 'calc(100vh - 120px)',
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    borderRadius: 12,
    height: 'calc(100vh - 120px)',
  } as CSSProperties,
  body: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 24,
    paddingRight: 8,
    fontSize: 14,
    overflow: 'hidden',
    height: 'calc(100% - 24px)',
  } as CSSProperties,
  header: {
    margin: 0,
    borderRadius: 12,
  } as CSSProperties,
};

const MOBILE_STYLES = {
  content: {
    padding: 0,
    borderRadius: 0,
    height: '100dvh',
    width: '100dvw',
    left: -8,
    display: 'flex',
    flexDirection: 'column',
  } as CSSProperties,
  body: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 16,
    fontSize: 14,
    overflow: 'hidden',
  } as CSSProperties,
};

const MODAL_PROPS = {
  desktop: {
    centered: true,
    width: 1100,
    styles: DESKTOP_STYLES,
  },
  mobile: {
    centered: true,
    width: '100dvw',
    height: '100dvh',
    style: { margin: 0 },
    styles: MOBILE_STYLES,
    closeIcon: false,
  },
};

export const VfoModalContainerV3: FC<ModalContainerProps> = ({ children, title, onClose }) => {
  const { t } = useTranslation('common');
  const { closeModal } = useModalsStore();
  const isDesktop = useIsDesktop();

  const variant = isDesktop ? 'desktop' : 'mobile';

  return (
    <ModalStyled
      open={true}
      onCancel={onClose ?? closeModal}
      onClose={onClose ?? closeModal}
      footer={null}
      closable={isDesktop}
      title={isDesktop && <ModalTitle title={title} />}
      closeIcon={<Icon name="x" size="s16" />}
      {...MODAL_PROPS[variant]}
    >
      {!isDesktop && (
        <>
          <HeadingContainer onClick={closeModal} style={{ cursor: 'pointer' }}>
            <Icon name="arrow-left" size="s20" />
            <span>{t('back')}</span>
            <TitleWrapper>
              <ModalTitle title={title} />
            </TitleWrapper>
          </HeadingContainer>
        </>
      )}
      {children}
    </ModalStyled>
  );
};

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 0 16px;
  font-weight: 700;
  cursor: pointer;
`;

const TitleWrapper = styled.div`
  width: 100%;
  text-align: center;
  padding-right: 60px;
`;

const ModalStyled = styled(Modal)`
  &&& {
    .ant-modal-close {
      border-radius: 50%;
      color: ${COLORS.systemDark200};
      background: ${COLORS.systemLight200};
      width: 24px;
      height: 24px;
      top: 16px;
      right: 24px;

      &:hover {
        background: ${COLORS.systemLight300};
      }
    }
  }
`;
